// PostHog follows the analytics choice in HubSpot's banner: without a yes it captures nothing and keeps
// its ids in memory only.

import { deleteCookie, hasAnalyticsConsent } from '@repo/utils';
import posthog, { PostHog, PostHogConfig } from 'posthog-js';

const STORED = 'localStorage+cookie';

// ids PostHog stored on an earlier visit, before consent was asked or before a withdrawal made elsewhere;
// with memory persistence it never reads them, so they are removed directly
function removeStoredIds(token: string): void {
  const name = `ph_${token}_posthog`;
  window.localStorage.removeItem(name);
  // reset() after memory persistence no longer clears PostHog's session copy
  window.sessionStorage.removeItem(name);
  deleteCookie(name);
}

function apply(instance: PostHog, analytics: boolean): void {
  if (analytics) {
    if (!instance.has_opted_in_capturing()) {
      instance.set_config({ persistence: STORED });
      instance.opt_in_capturing({ captureEventName: false });
    }
    return;
  }
  // opted out by default before any choice: nothing captured or stored to undo
  if (!instance.has_opted_out_capturing()) {
    instance.opt_out_capturing();
    // memory first, so the new id reset() makes is not stored; then the stored ids go at once
    instance.set_config({ persistence: 'memory' });
    instance.reset();
    removeStoredIds(instance.config.token);
  }
}

/** Start options: opted out until an analytics yes, which is applied once PostHog has loaded. */
export function postHogConsentOptions(analytics: boolean = hasAnalyticsConsent()): Partial<PostHogConfig> {
  return {
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    persistence: analytics ? STORED : 'memory',
    // the flags request carries the visitor's id and also brings remote config (recording, surveys);
    // the app reads no PostHog flags, and a yes given on the page gets them from the next page load
    advanced_disable_flags: !analytics,
    // runs before PostHog's first page view, so a choice changed since the last visit wins
    loaded: (instance) => {
      try {
        const accepted = hasAnalyticsConsent();
        if (!accepted) {
          removeStoredIds(instance.config.token);
        }
        apply(instance, accepted);
      } catch {
        // consent must never break the app
      }
    },
  };
}

/** Follows a change of choice. Before PostHog has loaded, its loaded callback applies the choice. */
export function setPostHogConsent(analytics: boolean): void {
  try {
    if (posthog.__loaded) {
      apply(posthog, analytics);
    }
  } catch {
    // consent must never break the app
  }
}
